import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MailCheck, Loader2, ArrowRight, Send } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { Logo } from "@/components/Brand";

export default function VerifyEmail() {
  const { token } = useParams();
  const { user, verifyEmail, resendVerification } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(token ? "verifying" : "info");
  const [error, setError] = useState("");
  const [resendEmail, setResendEmail] = useState(user?.email || "");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;
    verifyEmail(token)
      .then(() => setStatus("success"))
      .catch((err) => { setStatus("error"); setError(formatApiError(err)); });
  }, [token, verifyEmail]);

  const doResend = async (e) => {
    e.preventDefault();
    setResending(true);
    try {
      await resendVerification(resendEmail);
      toast.success("Verification email sent");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-white grid place-items-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(79,70,229,0.06),transparent_60%)] pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md relative">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center" data-testid="verify-card">
          {status === "verifying" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[#4F46E5] animate-spin mb-3" />
              <h1 className="font-display text-2xl font-semibold">Verifying your email…</h1>
              <p className="text-sm text-gray-500 mt-1.5">Just a moment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="font-display text-2xl font-semibold">Email verified</h1>
              <p className="text-sm text-gray-500 mt-2">You&apos;re all set. Head to your workspace.</p>
              <button
                onClick={() => nav(user?.role === "admin" ? "/admin" : "/portal")}
                data-testid="verify-continue"
                className="mt-6 inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-medium px-4 py-2.5 rounded-full transition-colors"
              >
                Continue <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 grid place-items-center mb-4">
                <XCircle className="w-6 h-6 text-rose-600" />
              </div>
              <h1 className="font-display text-2xl font-semibold">Verification failed</h1>
              <p className="text-sm text-gray-500 mt-2" data-testid="verify-error">{error || "This link may have expired."}</p>
              <div className="mt-6 max-w-xs mx-auto text-left">
                <form onSubmit={doResend} className="flex flex-col gap-2">
                  <input
                    type="email" required placeholder="you@school.edu"
                    value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                    data-testid="verify-resend-email"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm"
                  />
                  <button
                    disabled={resending} data-testid="verify-resend-submit"
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0F172A] disabled:bg-gray-400 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-[#1e293b] transition-colors"
                  >
                    {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {resending ? "Sending…" : "Resend verification"}
                  </button>
                </form>
              </div>
            </>
          )}

          {status === "info" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-indigo-50 grid place-items-center mb-4">
                <MailCheck className="w-6 h-6 text-indigo-600" />
              </div>
              <h1 className="font-display text-2xl font-semibold">Verify your email</h1>
              <p className="text-sm text-gray-500 mt-2">
                We&apos;ve sent a verification link{user?.email ? <> to <span className="font-medium text-gray-800">{user.email}</span></> : null}. Click it to activate your account.
              </p>
              <div className="mt-6 max-w-xs mx-auto text-left">
                <form onSubmit={doResend} className="flex flex-col gap-2">
                  <input
                    type="email" required placeholder="Your email"
                    value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                    data-testid="verify-resend-email"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm"
                  />
                  <button
                    disabled={resending} data-testid="verify-resend-submit"
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0F172A] disabled:bg-gray-400 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-[#1e293b] transition-colors"
                  >
                    {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {resending ? "Sending…" : "Resend verification email"}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          <Link to="/login" className="text-[#4F46E5] hover:underline">Back to sign in</Link>
        </div>
      </motion.div>
    </div>
  );
}
